<template>
  <div class="container mt-5">
    <div class="card modern-card">
      <div class="card-header">
        <h4 class="mb-0">
          Mobile Money - Rechargement via KkiaPay
        </h4>
      </div>

      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3 text-muted">Préparation du paiement...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          <i class="fas fa-exclamation-circle me-2"></i>
          {{ error }}
        </div>

        <div v-else>
          <div class="payment-info mb-4">
            <h5 class="info-title">Détails du rechargement</h5>
            <div class="info-content">
              <div class="info-row">
                <span class="info-label">Compte prépayé</span>
                <span class="info-value">{{ accountNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Solde actuel</span>
                <span class="info-value">{{ formatNumber(currentBalance) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Montant à recharger</span>
                <span class="info-value text-success fw-bold">{{ formatNumber(amount) }} {{ currency }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Nouveau solde estimé</span>
                <span class="info-value text-primary fw-bold">{{ formatNumber(currentBalance + amount) }} {{ currency }}</span>
              </div>
            </div>
          </div>

          <div class="alert alert-info-custom mb-4">
            <i class="fas fa-info-circle me-2"></i>
            Cliquez sur le bouton ci-dessous pour payer avec Mobile Money (MTN, Moov, Wave, etc.)
          </div>

          <div class="alert alert-warning mb-3">
            <i class="fas fa-flask me-2"></i>
            <strong>Mode TEST :</strong> Utilisez n'importe quel numéro pour simuler un paiement.
          </div>

          <div class="text-center my-4">
            <button 
              @click="openKkiaPay" 
              :disabled="processing"
              class="btn btn-primary btn-lg px-5 py-3">
              <i class="fas fa-mobile-alt me-2"></i>
              <span v-if="!processing">Payer {{ formatNumber(amount) }} {{ currency }}</span>
              <span v-else>
                <span class="spinner-border spinner-border-sm me-2"></span>
                Ouverture...
              </span>
            </button>
          </div>

          <div class="text-center mt-4">
            <router-link to="/listePrepaidAccount" class="btn btn-outline-secondary">
              <i class="fas fa-arrow-left me-2"></i>
              Annuler
            </router-link>
          </div>

          <div class="text-center mt-4">
            <small class="secure-badge">
              <i class="fas fa-lock me-1"></i>
              Paiement sécurisé par KkiaPay
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError, success as showSuccess } from '@/utils/utils';

declare global {
  interface Window {
    openKkiapayWidget: any;
    addKkiapayListener: any;
  }
}

export default defineComponent({
  name: 'RechargeKkiaPay',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const intentId = ref(route.query.intentId as string);
    const amount = ref(0);
    const currency = ref('EUROS');
    const accountNumber = ref('');
    const currentBalance = ref(0);
    const loading = ref(true);
    const error = ref('');
    const processing = ref(false);
    const kkiapayPublicKey = ref('');

    const formatNumber = (val: number) => new Intl.NumberFormat('fr-FR').format(val);

    const loadRechargeInfo = async () => {
      try {
        const { data } = await ApiService.get(`/prepaid/recharge/intent/${intentId.value}`);
        accountNumber.value = data.data.accountNumber;
        currentBalance.value = data.data.currentBalance;
        amount.value = data.data.amount;
        currency.value = data.data.currency;
        loading.value = false;
      } catch (err: any) {
        console.error('Erreur loadRechargeInfo:', err);
        error.value = 'Impossible de charger les informations';
        loading.value = false;
      }
    };

    // ✅ CORRECTION : Charger le SDK correctement
    const loadKkiaPayScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Vérifier si déjà chargé
        if (window.openKkiapayWidget) {
          console.log('✅ KkiaPay SDK déjà disponible');
          resolve();
          return;
        }

        console.log('📦 Chargement du SDK KkiaPay...');
        
        // Nettoyer les anciens scripts si présents
        const existingScript = document.querySelector('script[src*="kkiapay.me"]');
        if (existingScript) {
          existingScript.remove();
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.kkiapay.me/k.js';
        script.async = false; // ✅ IMPORTANT : charger de manière synchrone
        
        script.onload = () => {
          console.log('✅ Script KkiaPay chargé avec succès');
          
          // Attendre que l'objet soit disponible
          const checkAvailability = setInterval(() => {
            if (window.openKkiapayWidget) {
              clearInterval(checkAvailability);
              console.log('✅ KkiaPay SDK prêt !');
              resolve();
            }
          }, 100);
          
          // Timeout après 3 secondes
          setTimeout(() => {
            clearInterval(checkAvailability);
            if (!window.openKkiapayWidget) {
              console.error('❌ KkiaPay SDK non disponible après timeout');
              reject(new Error('KkiaPay SDK non disponible'));
            }
          }, 3000);
        };
        
        script.onerror = (e) => {
          console.error('❌ Erreur chargement script KkiaPay:', e);
          reject(new Error('Erreur de chargement du SDK KkiaPay'));
        };
        
        document.body.appendChild(script);
      });
    };

    // Initialiser KkiaPay (récupérer la clé publique)
    const initKkiaPay = async () => {
      try {
        console.log('🔑 Récupération de la clé publique KkiaPay...');
        const { data } = await ApiService.post('/prepaid/recharge/kkiapay/init', {
          intentId: intentId.value
        });

        kkiapayPublicKey.value = data.data.publicKey;
        console.log('✅ Clé publique récupérée');
        
      } catch (err: any) {
        console.error('❌ Erreur initKkiaPay:', err);
        error.value = 'Erreur lors de l\'initialisation de KkiaPay';
        throw err;
      }
    };

    // ✅ CORRECTION : Ouvrir le widget avec la bonne méthode
    const openKkiaPay = async () => {
      if (processing.value) return;
      processing.value = true;

      try {
        console.log('🔄 Préparation du paiement KkiaPay...');
        
        // Charger le SDK si pas encore fait
        if (!window.openKkiapayWidget) {
          await loadKkiaPayScript();
        }

        console.log('✅ Ouverture du widget KkiaPay...');

        // ✅ Configuration listener AVANT d'ouvrir le widget
        window.addKkiapayListener('success', async (response: any) => {
          console.log('✅ Paiement réussi:', response);
          
          try {
            await ApiService.post('/prepaid/recharge/kkiapay/verify', {
              transactionId: response.transactionId,
              intentId: intentId.value
            });
            
            showSuccess('Rechargement réussi ! Redirection...');
            setTimeout(() => router.push('/listePrepaidAccount'), 2000);
          } catch (err) {
            console.error('❌ Erreur vérification:', err);
            showError('Erreur lors de la confirmation du paiement');
          }
          processing.value = false;
        });

        window.addKkiapayListener('failed', (response: any) => {
          console.error('❌ Paiement échoué:', response);
          showError('Le paiement a échoué. Veuillez réessayer.');
          processing.value = false;
        });

        // ✅ Ouvrir le widget
        window.openKkiapayWidget({
          amount: amount.value,
          api_key: kkiapayPublicKey.value,
          sandbox: true,
          phone: '',
          data: JSON.stringify({
            intentId: intentId.value,
            accountNumber: accountNumber.value
          }),
          theme: '#0066ff'
        });

      } catch (err: any) {
        console.error('❌ Erreur openKkiaPay:', err);
        showError(err.message || 'Erreur lors de l\'ouverture de KkiaPay');
        processing.value = false;
      }
    };

    onMounted(async () => {
      console.log('🚀 Montage du composant RechargeKkiaPay');
      
      if (!intentId.value) {
        error.value = 'Lien invalide - intentId manquant';
        loading.value = false;
        return;
      }

      try {
        // Charger les informations
        await loadRechargeInfo();
        
        if (error.value) return;

        // Récupérer la clé publique
        await initKkiaPay();
        
        // ✅ Charger le SDK dès le montage
        await loadKkiaPayScript();
        
        console.log('✅ Tout est prêt pour KkiaPay');
      } catch (err: any) {
        console.error('❌ Erreur lors de l\'initialisation:', err);
        error.value = err.message || 'Erreur lors de l\'initialisation';
      }
    });

    return {
      accountNumber,
      currentBalance,
      amount,
      currency,
      loading,
      error,
      processing,
      formatNumber,
      openKkiaPay
    };
  }
});
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #0070ba 0%, #1546a0 100%);
  color: white !important;
  padding: 1.5rem;
  border: none;
}

.card-header h4 {
  font-weight: 600;
  font-size: 1.25rem;
  color: white !important;
}

.card-body {
  padding: 2rem;
}

.payment-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid #0070ba;
}

.info-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 2px solid #dee2e6;
}

.info-label {
  color: #6c757d;
  font-size: 0.95rem;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

.alert-info-custom {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 1rem;
  color: #0070ba;
}

.secure-badge {
  display: inline-flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.875rem;
}

.secure-badge i {
  color: #28a745;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25em;
}

@media (max-width: 576px) {
  .card-body {
    padding: 1.5rem;
  }
  
  .payment-info {
    padding: 1rem;
  }
}
</style>